import type { LensCatalogueDatasetSummary } from '~/types/leLens';

export type AnalyticsAreaId = 'commercial' | 'growth' | 'loyalty' | 'supply' | 'customer-service';

export type AnalyticsArea = {
  id: AnalyticsAreaId;
  name: string;
  description: string;
  status: 'available' | 'in_progress';
  statusLabel: string;
  firstRelease: string;
  questions: [string, string, string];
  plannedMetrics: [string, string, string];
  datasetMatch: (dataset: LensCatalogueDatasetSummary) => boolean;
};

const datasetIdentity = (dataset: LensCatalogueDatasetSummary) => `${dataset.name} ${dataset.topic ?? ''}`;

export const analyticsAreas: ReadonlyArray<AnalyticsArea> = [
  {
    id: 'commercial',
    name: 'Commercial',
    description: 'Sales, margin, orders and refunds across products and markets.',
    status: 'available',
    statusLabel: 'Metrics available',
    firstRelease: 'Current trading performance',
    questions: [
      'How are sales and margin tracking?',
      'Which products and markets moved?',
      'Where are refunds changing performance?',
    ],
    plannedMetrics: ['TTV and gross margin', 'Orders and average order value', 'Product and market movements'],
    datasetMatch: (dataset) =>
      ['Sales & margin', 'Bedbank', 'Demand & conversion'].includes(dataset.topic?.trim() ?? ''),
  },
  {
    id: 'growth',
    name: 'Growth',
    description: 'Profitable demand, acquisition, attribution and conversion in one governed growth view.',
    status: 'available',
    statusLabel: 'Metrics available',
    firstRelease: 'Paid media, GHA, email, demand and conversion',
    questions: [
      'Where is demand growing or falling?',
      'Which investment and communications are converting?',
      'Where are customers leaving the journey?',
    ],
    plannedMetrics: [
      'Marketing investment and returns',
      'Attribution and channel performance',
      'Demand, email and conversion',
    ],
    datasetMatch: (dataset) =>
      dataset.topic?.trim() === 'Demand & conversion' ||
      dataset.topic?.trim() === 'Marketing' ||
      /search|checkout|offer view|paid media|google hotel ads|paid campaign|marketing spend|email|communication|edm|message|sms|push notification|braze/i.test(
        datasetIdentity(dataset),
      ),
  },
  {
    id: 'loyalty',
    name: 'Loyalty',
    description: 'LuxPlus+ membership and Société loyalty programme activity.',
    status: 'available',
    statusLabel: 'Metrics available',
    firstRelease: 'Société points and LuxPlus+ renewal outcomes',
    questions: [
      'How many LuxPlus+ membership periods renewed, lapsed or were cancelled after renewal?',
      'How did LuxPlus+ ended-period outcomes change from the previous period?',
      'How many Société points were earned and burned by tier?',
    ],
    plannedMetrics: ['LuxPlus+ renewal outcomes', 'Société points earned and burned', 'Société points by tier'],
    datasetMatch: (dataset) => /luxplus|loyalty|société|societe|member/i.test(datasetIdentity(dataset)),
  },
  {
    id: 'supply',
    name: 'Supply & Offer Health',
    description: 'Accommodation availability, offer quality and demand gaps.',
    status: 'in_progress',
    statusLabel: 'Accommodation first',
    firstRelease: 'Accommodation supply',
    questions: [
      'Where is demand constrained by supply?',
      'Which offers are losing availability?',
      'Where can better supply unlock sales?',
    ],
    plannedMetrics: ['Searchable accommodation', 'Availability and offer health', 'Demand gaps by destination'],
    datasetMatch: (dataset) =>
      dataset.topic?.trim() === 'Bedbank' || /bedbank|hotel|accommodation|offer health|inventory/i.test(dataset.name),
  },
  {
    id: 'customer-service',
    name: 'Customer Service & Phone Sales',
    description: 'Customer contacts, service outcomes and phone-sales performance.',
    status: 'in_progress',
    statusLabel: 'Definitions in progress',
    firstRelease: 'Customer service and phone sales, reported separately',
    questions: [
      'Why are customers contacting us?',
      'How effectively are we resolving their needs?',
      'How are phone-sales teams converting demand?',
    ],
    plannedMetrics: ['Contact and service performance', 'Resolution and satisfaction', 'Phone-sales conversion'],
    datasetMatch: (dataset) =>
      /customer service|customer support|conversation|contact centre|contact center|phone sales|telesales|sales agent|lesa|genesys|zendesk/i.test(
        datasetIdentity(dataset),
      ),
  },
];

export const analyticsAreaById = (id: string | null | undefined): AnalyticsArea | undefined =>
  analyticsAreas.find((area) => area.id === id);

export const datasetsForAnalyticsArea = (
  area: AnalyticsArea,
  datasets: ReadonlyArray<LensCatalogueDatasetSummary>,
  curatedDatasetIds?: ReadonlyArray<string>,
): Array<LensCatalogueDatasetSummary> => {
  if (curatedDatasetIds) {
    const ids = new Set(curatedDatasetIds);
    return datasets.filter((dataset) => ids.has(dataset.id));
  }
  return datasets.filter(area.datasetMatch);
};
